import QorusClient from "@/api/clients/QorusClient";
import { LightProduct, Product, ProductsWithFilters } from "@/models/product";
import { configuration } from "@/config/InstanceConfiguration";
import { AxiosRequestConfig } from "axios";
import qs from "qs";

export interface ProductSearchParams {
    sort: string,
    sortOrder: string,
    itemsPerPage: number,
    page: number,
    filters: string
    searchTerm?: string
}

export enum ProductDomainCase {
    ByCategory,
    ByMerchant,
    ByType,
    Search
}

export const SORT_SEPERATOR = ","
/**
 *  Qorus API about products
 */
export default class ProductService {
    private qorusClient = QorusClient.of();

    static of(): ProductService {
        return new ProductService();
    }

    private abortController;

    private resetAbortController() {
        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();
        return this.abortController.signal;
    }

     /**
     * Gets all products according to a given pagination
     * @param endpoint endpoint to reach
     * @param params params of the page to retrieve
     * @return ProductsWithFilters Paginated products
     */
     private getProductsGeneric(endpoint: string, params: ProductSearchParams): Promise<ProductsWithFilters> {
        // A new instance of the AbortController is created before making the API request
        const signal = this.resetAbortController();

        // rename 'itemsPerPage' to 'size' to conform server API
        const adaptedParams = {
            ...params,
            page: params.page - 1,
            sort: params.sort || configuration.productList.navbar.defaultSort + SORT_SEPERATOR + configuration.productList.navbar.defaultSortOrder,
            filters: params.filters || null
        } as Partial<ProductSearchParams>;
        adaptedParams['size'] = params.itemsPerPage || configuration.productList.navbar.defaultItemsPerPage;
        delete adaptedParams.itemsPerPage;

        return this.qorusClient.get(endpoint, {
            signal,
            paramsSerializer: {
                serialize: (params) => {
                    return qs.stringify(params, { indices: false })
                },
                indexes: null
            },
            params: adaptedParams,
        }).then(
            (data: ProductsWithFilters) => {
                return data;
            },
            data => {
                throw data;
            },
        );
    }

    getProducts(domainCase: ProductDomainCase, domainParameter: string, params: ProductSearchParams): Promise<ProductsWithFilters> {
        switch (domainCase) {
            case ProductDomainCase.ByCategory: return this.getByCategory(domainParameter, params);
            case ProductDomainCase.ByMerchant: return this.getByMerchantId(domainParameter, params);
            case ProductDomainCase.ByType: return this.getByType(domainParameter, params);
            case ProductDomainCase.Search: return this.getBySearchTerm(domainParameter, params);

            default: throw new Error("unknown domain case " + domainCase);
        }
    }

    /**
     * Gets all products according to a given pagination
     * @param categoryId category Id
     * @param page Page to retrieve
     * @param pageSize Number of products by page
     * @param sort Attribute used for sorting
     * @param asc True (ascending) / False (descending)
     * @return Page<LightProductDTO> Paginated products
     */
    getByCategory(categoryId: string, query: any): Promise<ProductsWithFilters> {
        return this.getProductsGeneric("products/category", { ...query, categoryPath: categoryId })
    }

    getByMerchantId(merchantId: string, query: any): Promise<ProductsWithFilters> {
        return this.getProductsGeneric("products/seller", query)
    }

    getByType(type: string, query: any): Promise<ProductsWithFilters> {
        return this.getProductsGeneric("products/category", { ...query, filters: query.filters ? query.filters + `&${type}:true` : `${type}:true` })
    }

    getBySearchTerm(searchTerm: string, query: ProductSearchParams): Promise<ProductsWithFilters> {
        if (!searchTerm)
            return Promise.resolve(ProductsWithFilters.empty())

        return this.getProductsGeneric("products/search", { ...query, searchTerm: searchTerm })
    }

    getBySlug(slug: string): Promise<Product> {
        return this.qorusClient.get(`products/slug/` + slug)
        .then(
            (data: Product) => {
                return Product.of(data);
            },
            data => {
                throw data;
            },
        );
    }

    search(searchTerm: string): Promise<ProductsWithFilters> {
        const axiosRequestConfig: AxiosRequestConfig = {
            params: {
                searchTerm,
                size: 4,
            },
        };
        return this.qorusClient.get(`products/search`, axiosRequestConfig).then(
            (data: ProductsWithFilters) => {
                return data;
            },
            data => {
                throw data;
            },
        );
    }

    searchAutocomplete(searchTerm: string): Promise<Array<string>> {
        const axiosRequestConfig: AxiosRequestConfig = {
            params: {
                searchTerm,
                size: 10,
            },
        };
        return this.qorusClient.get(`products/search-autocomplete`, axiosRequestConfig).then(
            (data) => {
                return data.map(p => LightProduct.of(p))
            },
            data => {
                throw data;
            },
        );
    }

    getBySkuList(referenceList: string): Promise<any> {
        const axiosRequestConfig: AxiosRequestConfig = {
            params: {
                referenceList,
            },
        };
        return this.qorusClient.get(`products/references`, axiosRequestConfig).then(
            (data) => {
                return data;
            },
            err => {
                throw err;
            },
        );
    }

    restockAlert(form): Promise<any> {
        return this.qorusClient.post("products/offer/restock-alert", form).then(
            (data: any) => {
                return data;
            },
            data => {
                throw data;
            },
        );
    }
}